import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Badge,
  Button,
} from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import Loader from "../../Components/Common/Loader";
import { getTransactionsQuery, getTerminslsQuery } from "../../queries";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import Flatpickr from "react-flatpickr";
import { getFlatpickrLocale } from "../../utils/flatpickr";
import Select from "react-select";
import { getTransactions } from "../../api/seller";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import Widgets from "../../pages/DashboardEcommerce/Widgets";

export const Transactions = ({ sellerId, hideStats = false }) => {
  const [stats, setStats] = useState({});
  const [terminal, setTerminal] = useState();
  const [terminalOptions, setTerminalsOptions] = useState([]);
  const [date, setDate] = useState();
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const { t, i18n } = useTranslation();

  const fpRef = React.useRef(null);

  useEffect(() => {
    if (fpRef.current && fpRef.current.flatpickr && fpRef.current.flatpickr.altInput) {
      fpRef.current.flatpickr.altInput.placeholder = t("Select a date");
    }
  }, [t]);

  const flatpickrLocale = getFlatpickrLocale(i18n.language);

  const { data: transactions, isLoading } = useQuery({
    ...getTransactionsQuery({
      page,
      fromCreatedAt: date?.[0],
      toCreatedAt: date?.[1],
      terminalId: terminal?.value,
      sellerId,
    }),
  });

  const exportTransactions = async () => {
    setIsExporting(true);
    try {
      const res = await getTransactions({
        size: 1000000,
        page: 1,
        fromCreatedAt: date?.[0],
        toCreatedAt: date?.[1],
      });

      const data = res.data.data;

      const formattedData = data.map((transaction) => ({
        [t("TID")]:
          transaction?.terminal?.posHardwareSerialNumber ||
          `${transaction?.paymentTask?.serviceName}-${transaction?.paymentTask?.sellerFoundBy || ""
          }`,
        [t("Transaction number (payment by terminal)")]: transaction.incomeExt,
        [t("Type of operation")]: t("Payment"),
        [t("Time")]: dayjs(transaction.createdAt).format("DD.MM.YYYY, HH:mm:ss"),
        [t("Payment amount")]: `${(transaction.paymentAmount / 100)}`,
        [t("Transaction number (seller card top up)")]: transaction.outgoingExt,
        [t("Card number")]: transaction.sellerCardNumber.maskedNumber,
        [t("Type of operation")]: "bo.trans.credit",
        [t("Top up amount")]: `${(
          transaction.amount / 100
        )}`,
        [t("Comission")]: `${(
          (transaction.amount - transaction.paymentAmount) /
          100
        )}`,
      }));

      // Add totals (stats) as the first row
      const summaryRow = {
        [t("TID")]: t("Total (summary)"),
        [t("Transaction number (payment by terminal)")]: "",
        [t("Type of operation")]: "",
        [t("Time")]: "",
        [t("Payment amount")]: `${(
          stats.totalAmountPayment / 100
        ).toLocaleString()} uzs`,
        [t("Transaction number (seller card top up)")]: "",
        [t("Card number")]: "",
        [t("Type of operation")]: "",
        [t("Top up amount")]: `${(
          stats.totalAmountTransactions / 100
        ).toLocaleString()} uzs`,
        [t("Comission")]: `${(
          stats.totalAmountCommissions / 100
        ).toLocaleString()} uzs`,
      };

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet([summaryRow, ...formattedData]);
      XLSX.utils.book_append_sheet(wb, ws, t("Transactions"));
      XLSX.writeFile(wb, `sverka-${Date.now()}.xlsx`);
    } catch (error) {
      console.error("Error exporting transactions:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const { data: terminals } = useQuery({
    ...getTerminslsQuery({
      page: 1,
      size: 1000,
    }),
  });

  const columns = useMemo(
    () => [
      {
        header: t("TID"),
        cell: ({ row }) =>
          row.original.terminal?.posHardwareSerialNumber ||
          `${row.original.paymentTask.serviceName}-${row.original.paymentTask.sellerFoundBy}`,
        enableColumnFilter: false,
      },
      {
        header: t("Time"),
        cell: ({ row }) =>
          dayjs(row.original.createdAt).format("DD.MM.YYYY, HH:mm:ss"),
      },
      {
        header: t("Payment amount"),
        cell: ({ row }) =>
          `${(row.original.paymentAmount / 100).toLocaleString()} uzs`,
        enableColumnFilter: false,
      },
      {
        header: t("Card number"),
        cell: ({ row }) => row.original.sellerCardNumber?.maskedNumber,
        enableColumnFilter: false,
      },
      {
        header: t("Top up amount"),
        cell: ({ row }) =>
          `${(row.original.amount / 100).toLocaleString()} uzs`,
        enableColumnFilter: false,
      },
      {
        header: t("Comission"),
        cell: ({ row }) =>
          `${(
            (row.original.paymentAmount - row.original.amount) /
            100
          ).toLocaleString()} uzs`,
        enableColumnFilter: false,
      },
      {
        header: t("Status"),
        cell: ({ row }) =>
          row.original.status === "completed" ? (
            <Badge color="success">{t(row.original.status)}</Badge>
          ) : row.original.status === "failed" ? (
            <Badge color="danger">{t(row.original.status)}</Badge>
          ) : (
            <Badge color="secondary">{row.original.status}</Badge>
          ),
        enableColumnFilter: false,
      },
    ],
    [t]
  );

  useEffect(() => {
    if (terminals) {
      setTerminalsOptions(
        terminals?.data?.data.map((terminal) => ({
          value: terminal.id,
          label: terminal.name,
        }))
      );
    }
  }, [terminals]);

  const clearFilters = () => {
    setTerminal(null);
    setDate(null);
  };

  useEffect(() => {
    setPage(1);
  }, [date, terminal]);

  return (
    <Row>
      <Col lg={12}>
        {!hideStats && (
          <Row>
            <Widgets dateRange={date} setData={setStats} sellerId={sellerId} />
          </Row>
        )}
        <Card id="terminalList">
          <CardHeader className="border-0">
            <Row className="g-4 align-items-center">
              <div
                className="col-sm mb-3"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <h5 className="mb-0 fw-bold">{t("Sverka")}</h5>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "0.7fr 1fr 40px auto",
                    gap: "20px",
                    width: "600px",
                  }}
                >
                  <Select
                    value={terminal}
                    options={terminalOptions}
                    onChange={(selectedOption) => setTerminal(selectedOption)}
                    placeholder={t("Terminals")}
                  />
                  <Flatpickr
                    ref={fpRef}
                    name="orderDate"
                    className="form-control"
                    placeholder={t("Select a date")}
                    onChange={(e) => setDate(e)}
                    value={date}
                    options={{
                      enableTime: true,
                      altInput: true,
                      mode: "range",
                      altFormat: "d M, Y, G:i K",
                      dateFormat: "d M, Y, G:i K",
                      locale: flatpickrLocale,
                    }}
                  />
                  <Button
                    style={{ padding: 0 }}
                    onClick={exportTransactions}
                    disabled={isExporting}
                  >
                    <i
                      className="ri-file-excel-2-line"
                      style={{ fontSize: "18px" }}
                    />
                  </Button>
                  {(date?.[0] || terminal) && (
                    <Button color="danger" onClick={clearFilters}>
                      {t("Clear filters")}
                    </Button>
                  )}
                </div>
              </div>
            </Row>
          </CardHeader>
          <div className="card-body pt-0">
            <div>
              <table style={{ width: "100%" }}>
                <thead></thead>
              </table>
              {transactions?.data?.data.length > 0 && (
                <TableContainer
                  pagination={{
                    setPage: setPage,
                    page: page,
                    total: transactions?.pagination.TotalPages,
                  }}
                  columns={columns}
                  data={transactions?.data?.data || []}
                  className="custom-header-css"
                  divClass="table-responsive table-card mb-1"
                  tableClass="align-middle table-nowrap"
                  theadClass="table-light text-muted"
                />
              )}
              {transactions?.data?.data.length === 0 && (
                <p className="mt-2" style={{ textAlign: "center" }}>
                  {t("Informations not found")}
                </p>
              )}
              {isLoading || isExporting ? <Loader /> : null}
            </div>
          </div>
        </Card>
      </Col>
    </Row>
  );
};
